import React, { useState, Dispatch, FC, SetStateAction, useEffect } from "react"
import axios from 'ps-components/lib/apiClient';
import ActionableHeaderCard from 'ps-components/Card/ActionableHeaderCard';
import Search from 'ps-components/Search/Search';
import LoadingIndicator from 'ps-components/LoadingIndicator/LoadingIndicator';
import { useQuery } from "react-query";
import { useQueryParams } from '../../../hooks/util'
import { GetParticipationSurveysResponseData, ParticipationReport, Survey } from "./types";
import { useSelector } from "react-redux";
import { IAppState } from "../../../lib/types";

type Props = {
  selectedSurvey?: Survey
  setSelectedSurvey: Dispatch<SetStateAction<Survey | undefined>>
  setSelectedReport: Dispatch<SetStateAction<ParticipationReport | undefined>>
}

export const SurveysCard: FC<Props> = ({ selectedSurvey, setSelectedSurvey, setSelectedReport }) => {
  const [filteredSurveys, setFilteredSurveys] = useState<Survey[]>([])
  const [surveySearch, setSurveySearch] = useState<string>('')
  const [showAllPreviousSurveys, setShowAllPreviousSurveys] = useState<boolean>(false)
  const query = useQueryParams()
  const { masterAccountId } = useSelector((state: IAppState) => state.session)
  const surveySourceId = query.get('pub_survey_source_id')
  const [isOpen, setIsOpen] = useState<boolean>(false)

  const fetchSurveys = async (): Promise<Survey[]> => {
    const response = await axios.apiGet(`/cms/api/accounts/${masterAccountId}/getPreviousYearSurvey?surveySourceId=${surveySourceId}&includeAll=true`)
    if (!response.data || !response.data.length) return []
    const surveys = response.data.map((s: GetParticipationSurveysResponseData) => ({
      code: s.pub_survey_code,
      source: s.pub_survey_short_name,
      publisher: s.pub_survey_publisher,
      year: s.survey_year
    }))
    return surveys
  }

  const { data, isLoading } = useQuery(['P_CENTER_SURVEYS', { surveySourceId, includeAll: showAllPreviousSurveys }], fetchSurveys, { refetchOnWindowFocus: false, retry: 1 })

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchVal = e.target.value
    setSurveySearch(searchVal)
    setSelectedSurvey(undefined)
    setSelectedReport(undefined)
    const filteredSurveys = data?.filter((survey) => {
      const lowerSearch = searchVal.toLowerCase()
      return (
        survey.code?.toLowerCase().includes(lowerSearch) ||
        survey.publisher?.toLowerCase().includes(lowerSearch) ||
        survey.source?.toLowerCase().includes(lowerSearch) ||
        survey.year?.toString().includes(lowerSearch)
      )
    })
    setFilteredSurveys(filteredSurveys || [])
  }

  const clearSearch = () => {
    setSurveySearch('')
    setSelectedSurvey(undefined)
    setSelectedReport(undefined)
    setFilteredSurveys(data || [])
  }

  const handleSelect = (survey: Survey) => {
    setIsOpen(false)
    if (survey.code !== selectedSurvey?.code) {
      setSelectedSurvey(survey)
      setSelectedReport(undefined)
    }
  }

  useEffect(() => {
    if (data) {
      setFilteredSurveys(data)
      if (data.length === 1) setSelectedSurvey(data[0])
    }
  }, [data])

  if (isLoading) return <LoadingIndicator loading={isLoading} />

  return (
    <ActionableHeaderCard cardTitle={!selectedSurvey ? 'Select Survey Code' : `Survey Code: ${selectedSurvey.code}`} isCollapsible={true} isOpen={isOpen}>
      <div className='pcenter-slideout__search_container'>
        <div className='pcenter-slideout__search_input'>
          <Search placeholder='Search' onClick={clearSearch} onChange={handleSearch} value={surveySearch} />
        </div>
      </div>
      <div className="pcenter-slideout__table_container" style={{ height: !selectedSurvey ? '60vh' : '23vh' }}>
        <table className='pcenter-slideout__table'>
          <tbody>
            {filteredSurveys.map((s, i) => (
              <tr key={i} className={s.code === selectedSurvey?.code ? 'pcenter-slideout__table_row_selected' : 'pcenter-slideout__table_row'} onClick={() => handleSelect(s)}>
                <td className='pcenter-slideout__table_data'>{s.code}</td>
                <td className='pcenter-slideout__table_data'>{s.source}</td>
                <td className='pcenter-slideout__table_data'>{s.publisher}</td>
                <td className='pcenter-slideout__table_data'>{s.year}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredSurveys.length === 0 && surveySearch.trim().length > 1 &&
        <div className='pcenter-slideout__no_surveys_text'>
          No Surveys found with the search value of {surveySearch}
        </div>
      }
    </ActionableHeaderCard>
  )
}
